import { useState, useCallback, useEffect, useRef } from 'react';
import { getLocalStorageJSON, setLocalStorageJSON } from '../utils/HelperFunction';

interface UsePersistedFiltersOptions {
    moduleName: string;
    defaultFilter: string;
    validFilters: string[];
    isArchived?: boolean;
    persistState?: boolean;
}

export function usePersistedFilters({
    moduleName,
    defaultFilter,
    validFilters,
    isArchived = false,
    persistState = true,
}: UsePersistedFiltersOptions) {

    const shouldPersist = persistState && !isArchived;

    const getPersistedSelectedFilter = () => {
        if (!shouldPersist) return defaultFilter;

        const saved = localStorage.getItem(`${moduleName}_selectedFilter`);
        if (!saved || !validFilters.includes(saved)) return defaultFilter;

        // If no saved filter payload exists for the saved tab, fallback to the default tab.
        // This keeps the initial state aligned with expected default accessible filter behavior.
        const hasSavedActiveFilter = localStorage.getItem(`${moduleName}_${saved}_activeFilter`) !== null;
        const hasSavedFormData = localStorage.getItem(`${moduleName}_${saved}_formData`) !== null;

        return (hasSavedActiveFilter || hasSavedFormData) ? saved : defaultFilter;
    };

    const [filterValues, setFilterValuesInternal] = useState<any[]>(
        () => shouldPersist ? getLocalStorageJSON(`${moduleName}_filterValues`, []) : []
    );

    const [selectedFilter, setSelectedFilterInternal] = useState<string>(() => getPersistedSelectedFilter());

    // Keep a ref in sync so setActiveFilter always reads the latest tab.
    // Updated synchronously in setSelectedFilter (not via useEffect) to guarantee
    // the ref is current even when child-component effects call setActiveFilter
    // before this component's own effects run.
    const selectedFilterRef = useRef(selectedFilter);

    const initialTab = getPersistedSelectedFilter();

    const [activeFilter, setActiveFilterInternal] = useState<any>(
        () => shouldPersist ? getLocalStorageJSON(`${moduleName}_${initialTab}_activeFilter`, null) : null
    );

    // Reset filter chips when entering/leaving archived view
    const prevArchivedRef = useRef(isArchived);
    useEffect(() => {
        if (prevArchivedRef.current !== isArchived) {
            prevArchivedRef.current = isArchived;
            // Defer state updates to prevent concurrent update errors
            Promise.resolve().then(() => {
                setFilterValuesInternal(shouldPersist ? getLocalStorageJSON(`${moduleName}_filterValues`, []) : []);
                if (!shouldPersist) {
                    selectedFilterRef.current = defaultFilter;
                    setSelectedFilterInternal(defaultFilter);
                    setActiveFilterInternal(null);
                }
            });
        }
    }, [isArchived, moduleName, shouldPersist, defaultFilter]);

    const setFilterValues = useCallback((data: any[]) => {
        setFilterValuesInternal(data);
        if (shouldPersist) {
            setLocalStorageJSON(`${moduleName}_filterValues`, data);
        }
    }, [moduleName, shouldPersist]);

    const setSelectedFilter = useCallback((filter: string) => {
        selectedFilterRef.current = filter;
        setSelectedFilterInternal(filter);
        if (shouldPersist) {
            localStorage.setItem(`${moduleName}_selectedFilter`, filter);
        }
    }, [moduleName, shouldPersist]);

    // Keep activeFilter aligned with the currently selected accessible tab.
    // This prevents stale tab filters from being reused during site-change refetches.
    useEffect(() => {
        if (!shouldPersist) return;

        // Use a microtask to defer state update and avoid concurrent update errors
        const update = () => {
            if (isArchived) {
                setActiveFilterInternal(null);
                return;
            }

            const key = `${moduleName}_${selectedFilter}_activeFilter`;
            const nextActive = getLocalStorageJSON(key, null);
            setActiveFilterInternal(nextActive);
        };

        Promise.resolve().then(update);
    }, [moduleName, selectedFilter, isArchived, shouldPersist]);

    const setActiveFilter = useCallback((filterData: any | null, tab?: string) => {
        if (isArchived) {
            setActiveFilterInternal(null);
            return;
        }
        setActiveFilterInternal(filterData);
        const activeTab = tab ?? selectedFilterRef.current;
        const key = `${moduleName}_${activeTab}_activeFilter`;
        if (shouldPersist) {
            if (filterData !== null) {
                setLocalStorageJSON(key, filterData);
            } else {
                localStorage.removeItem(key);
            }
        }
    }, [isArchived, moduleName, shouldPersist]);

  

    return {
        filterValues,
        selectedFilter,
        activeFilter,
        setFilterValues,
        setSelectedFilter,
        setActiveFilter,
    };
}
